import {
    DataSource, SelectionModel
} from "@angular/cdk/collections";
import { IListStateService } from "../interfaces/list-state-service";
import {
    BehaviorSubject, catchError, combineLatest, Connectable, connectable, filter, map,
    merge, Observable, of, skip, Subject, Subscription, switchMap, take, tap
} from "rxjs";
import { IListDataService } from "../interfaces/list-data-service";
import { IListFilterRequest } from "../interfaces/list-filter-request";
import {
    DestroyRef, inject, Injectable, OnDestroy
} from "@angular/core";
import { IListFilterResponse } from "../interfaces/list-filter-response";
import { ListDataSourceException } from "../../exceptions/list-data-source-exception";
import {
    LIST_DATA_SERVICE, LIST_STATE_SERVICE
} from "../constants/tokens";
import {
    ArrayUtil, ILocalizableModel, ObjectUtil
} from "@shopliftr/common-js/data";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import { RowStateType } from "../enums/row-state-type";
import { RxjsUtil } from "../../utils/rxjs-util";
import { AbstractLocalizedListStateService } from "./abstract-localized-list-state-service";
import { ILocalizedListStateService } from "../interfaces/localized-list-state-service";
import { ListUtil } from "../util/list-util";
import { IListActionEvent } from "../interfaces/list-action-event";

/**
 * A data source implementation for Angular Material tables that handles data loading, filtering, and selection.
 *
 * @template M The model type for the list items
 * @template O The options type for filtering and configuration
 * @template R The row type emitted by the data source (defaults to M; use Localized<M> for localized lists)
 *
 * Features:
 * - Integrates with Angular Material's MatTable
 * - Manages data selection state
 * - Handles data filtering and pagination
 * - Supports row state management
 * - Provides reactive data updates
 * - Supports on-the-fly row localization via AbstractLocalizedListStateService
 *
 * @implements {DataSource<R>}
 * @implements {OnDestroy}
 *
 * @see IListStateService For state management
 * @see IInternalApiService For data retrieval or
 * @see IListDataService For data retrieval
 */
@Injectable()
export class ListDataSourceService<
    M extends object,
    O extends object,
    R extends object = M
> implements DataSource<R>, OnDestroy {

    /**
     * Selection model that tracks selected items in the list.
     * Supports multiple selection and uses deep object comparison.
     */
    readonly selectedData = new SelectionModel<M>(true, [], true, (o1, o2) => ObjectUtil.equals(o1, o2));

    /**
     * Checks if all items in the current data set are selected.
     * @returns True if all items are selected, false otherwise
     */
    get isAllSelected(): boolean {

        if (ArrayUtil.isEmpty(this.data) || this.selectedData.isEmpty()) {
            return false;
        }

        return this.data.length === this.selectedData.selected.length
            && this.data.every((value) => this.selectedData.isSelected(ListUtil.unwrapRow<M>(value)));
    }

    /**
     * Gets the current data array as row type R (may be localized clones when a localized state service is used).
     * @returns The current list of items, or empty array if no data
     */
    get data(): Array<R> {
        return (this._data ?? []) as unknown as Array<R>;
    }

    /**
     * Gets the observable stream of data updates.
     * @returns Observable that emits the current data array when it changes
     */
    get data$(): Observable<Array<R>> {
        return this._dataConnectable$;
    }

    /**
     * Checks if the current data set is empty.
     * @returns True if there are no items in the data array
     */
    get isEmpty(): boolean {
        return ArrayUtil.isEmpty(this.data);
    }

    /**
     * True once a load has completed on the current connection.
     *
     * The connectable is seeded with an empty array so the table has something to render
     * before the first response arrives, which means an empty `data` cannot be read as
     * "the list loaded and returned nothing". This flag separates the two: it stays false
     * for the seeded emission and flips on the first emission produced by the pipeline —
     * a cache hit, fetched results, pinned models, a localized re-emission or an error
     * path. It is reset whenever the connectable is rebuilt, so a table that disconnects
     * and reconnects starts from false again.
     *
     * @returns True when the data currently held came from a completed load.
     */
    get loaded(): boolean {
        return this._loaded;
    }

    /**
     * Internal storage for the current data array
     */
    private _data: Array<M>;

    /**
     * Backs {@link loaded}. Set when the pipeline emits, reset when the connectable is rebuilt.
     */
    private _loaded = false;

    /**
     * Connectable observable that emits data updates (rows of type R)
     */
    private _dataConnectable$: Connectable<Array<R>>;

    /**
     * Subscription to the connectable observable
     */
    private _dataConnectableSubscription: Subscription;

    /**
     * Subject used to trigger list reload from the reloadList function
     *
     * Completed when the component is destroyed from the provided $destroyRef;
     */
    private readonly _reload$ = new Subject<IListFilterRequest<O>>();

    /**
     * List state service used to get list filtering parameters
     */
    private readonly _stateService = inject(LIST_STATE_SERVICE) as IListStateService<M, O>;

    /**
     * When the injected state service extends AbstractLocalizedListStateService, this holds a typed
     * reference used to wire language changes into the connectable pipeline. Otherwise undefined.
     */
    private readonly _localizedState: ILocalizedListStateService<M & ILocalizableModel, O> | undefined;

    /**
     * Service used to retrieve the data for list filtering can either be an instance of InternalAPIService
     * or an instance of IListDataService
     */
    private readonly _dataService = inject(LIST_DATA_SERVICE) as IListDataService<M, O>;

    /**
     * Application state service used to handle the processing state
     */
    private readonly _appState = inject(COMMON_APP_SERVICE);

    /**
     * Error handler used to log and handle errors
     */
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    /**
     * Reference used for automatic subscription cleanup
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Constructor for the data source
     *
     * Sets up the Connectable observable and the rowStateChange subscriptions
     */
    constructor() {

        if (this._stateService instanceof AbstractLocalizedListStateService) {
            this._localizedState = this._stateService as unknown as ILocalizedListStateService<M & ILocalizableModel, O>;
        }
        this._setupRefresh();
        this._setupConnectable();
        this._setupRowStateChange();
        this._setupListAction();
    }

    /**
     * This will ensure that all subjects are completed when the data source is destroyed
     */
    ngOnDestroy(): void {

        this._dataConnectableSubscription?.unsubscribe();
        this._reload$.complete();
    }

    /**
     * Connect function called by the MatTable to retrieve data
     */
    // eslint-disable-next-line @smarttools/rxjs/finnish -- required by Angular CDK DataSource interface; cannot be renamed
    connect(): Observable<Array<R>> {

        if (!this._dataConnectableSubscription) {
            this._dataConnectableSubscription = this._dataConnectable$.connect();
        }
        return this.data$;
    }

    /**
     * Disconnect function called by the MatTable when the component is destroyed
     */
    disconnect(): void {

        if (this._dataConnectableSubscription) {
            this._dataConnectableSubscription.unsubscribe();
            this._dataConnectableSubscription = undefined;
            this._setupConnectable();
        }
    }

    /**
     * Refreshes the list data by retrieving it from the filter service
     *
     * The optional beforeReloadList function will be called first which must return true to continue with the .
     */
    reloadList(beforeBeforeReloadList?: (request: IListFilterRequest<O>) => Observable<boolean> | boolean): void {

        try {

            let response$: boolean | Observable<boolean> = true;
            if (beforeBeforeReloadList) {
                response$ = beforeBeforeReloadList(this._stateService.filterRequest);
            }

            if (response$ instanceof Observable) {
                // This response will complete
                response$.pipe(
                    // Complete after the first emit next
                    take(1),
                    // Only true events will emit next
                    filter((value) => value),
                    takeUntilDestroyed(this._destroyRef)
                ).subscribe({
                    // Send the refresh
                    next: () => {
                        this._stateService.clearCache();
                        this._stateService.clearPinnedModels();
                        this._reload$.next(this._stateService.filterRequest);
                    },
                    // Send an error if the observable errors
                    error: (error: unknown) => this._errorHandler.handleServiceError(error, "Failed to refresh list")
                });
            }
            else if (response$) {
                // Clear cache, release any pin, then send the refresh
                this._stateService.clearCache();
                this._stateService.clearPinnedModels();
                this._reload$.next(this._stateService.filterRequest);
            }
        }
        catch (error) {
            // Send an error on error
            this._errorHandler.handleServiceError(error, "Failed to refresh list");
        }
    };

    /**
     * Select all of the data in the selectedData model.
     * When the data source emits localized rows (R = Localized<M>), each row is unwrapped to its
     * raw model before being added to selectedData (which always holds SelectionModel<M>).
     */
    selectAll(): void {

        this.selectedData.select(
            ...this.data
                .filter((value) => this._stateService.getRowState(value as unknown as M)?.type !== RowStateType.Disabled)
                .map((value) => ListUtil.unwrapRow<M>(value))
        );
    }

    /**
     * Calls filter list on the filterService
     * It sets the searchTotal in the stateService
     * It returns an observable of the results
     *
     * Cache read-through: when the state service has a cached entry matching the request,
     * the result is returned immediately without a network call. No `of([])` prefix is emitted,
     * so the table keeps the previous data visible while any in-flight request completes.
     */
    private _filterList$(request: IListFilterRequest<O>): Observable<Array<M>> {

        try {
            // Cache hit — return immediately, no network request.
            const cached = this._stateService.readCache(request);
            if (cached) {
                this._stateService.updateSearchTotal(cached.total);
                return of(cached.results);
            }

            if (request) {
                // Cache miss — fetch then write through. No of([]) prefix: the table
                // keeps previous data visible while the request is in-flight.
                return this._dataService.filterList$(request).pipe(
                    // Set processing state
                    RxjsUtil.appProcessingUntilDone(this._appState),
                    // Ensure we only get 1 response and then complete
                    take(1),
                    // Map to the results and write through to cache
                    map((response: IListFilterResponse<M>) => this._processResponse(request, response)),
                    // If there is an error send it to the _errorHandler and return an empty array
                    catchError((error: unknown) => this._handleFilterError$(error, new ListDataSourceException("Failed to filter list")))
                );
            }
            else {
                // Returns an empty array in an observable
                return of(this._processResponse(undefined, undefined));
            }
        }
        catch (error) {
            // If there is an error in the send it to the _errorHandler and return an empty array in an observable
            return this._handleFilterError$(error, new ListDataSourceException("Failed to filter list"));
        }
    }

    /**
     * If there is an error when filtering the list
     * Send it to the onError$ observable for handling in the controller
     *
     * Return and empty array in response so the list continues to function
     *
     */
    private _handleFilterError$(originalError: unknown, newError: ListDataSourceException): Observable<Array<M>> {

        // Log the original Error
        this._errorHandler.handleServiceError(originalError, newError.message);
        return of(this._processResponse(undefined, undefined));
    }

    /**
     * Process the response from the filterList function:
     * - Updates the searchTotal in the stateService
     * - Writes the result to the cache when the state service has caching enabled
     * - Returns the result array
     *
     * If the response is not provided it will reset the searchTotal to 0 and return an empty array as a result.
     * This ensures that the list will continue to function on error paths.
     */
    private _processResponse(request?: IListFilterRequest<O>, response?: IListFilterResponse<M>): Array<M> {

        if (response) {
            const total = response.total ?? response.results?.length ?? 0;
            this._stateService.updateSearchTotal(total);
            this._stateService.writeCache(request, response.results, total);
            return response.results;
        }
        else {
            this._stateService.updateSearchTotal(0);
            return [];
        }
    }

    /**
     * Sets up a Connectable to be used to multi-cast after the connect function is called.
     *
     * When the injected state service is an AbstractLocalizedListStateService, the pipeline uses
     * combineLatest to re-emit localized rows on every language change without triggering a new
     * HTTP request. Otherwise it passes raw model rows through directly.
     *
     * When the state service has pinned models, the pipeline short-circuits the HTTP request and
     * emits the pinned models directly. The pin is released by calling clearPinnedModels() which
     * emits an empty array on pinnedModels$, causing the pipeline to resume normal filtering.
     *
     * Every emission also records the rendered row positions on the state service via
     * `setRowPositions`, which backs `ListDeleteBehavior.ScrollToSpot`.
     */
    private _setupConnectable(): void {

        this._loaded = false;

        // `pinnedModels$` replays its current value on subscribe, and so does `filterRequest$`: without
        // skipping the replay, the first load sent two requests back to back and aborted the first.
        const raw$ = merge(
            this._reload$,
            this._stateService.pinnedModels$.pipe(skip(1)),
            this._stateService.filterRequest$
        ).pipe(
            switchMap(() => {
                if (this._stateService.isPinned) {
                    return of(this._stateService.pinnedModels as unknown as Array<M>);
                }
                return this._filterList$(this._stateService.filterRequest);
            }),
            takeUntilDestroyed(this._destroyRef)
        );

        let rows$: Observable<Array<R>>;
        if (this._localizedState) {
            const localizedState = this._localizedState;
            rows$ = combineLatest([raw$, localizedState.selectedLanguage$]).pipe(
                map(([rawRows]) => rawRows.map(
                    (row) => localizedState.localizeRow(row as unknown as M & ILocalizableModel) as unknown as R
                ))
            );
        }
        else {
            rows$ = raw$ as unknown as Observable<Array<R>>;
        }

        const source$ = rows$.pipe(
            tap((current) => {
                this.selectedData.clear();
                this._loaded = true;
                this._data = current as unknown as Array<M>;
                // Single funnel point for every load path, so row positions stay in step with
                // what is rendered and a pending scroll-to-position resolves here.
                this._stateService.setRowPositions(current as unknown as Array<M>);
            })
        );

        this._dataConnectable$ = connectable(source$, { connector: () => new BehaviorSubject<Array<R>>([]) });
    }

    /**
     * Sets up a subscription to handle row state changes.
     *
     * When the row state changes in the state service:
     * - Checks all currently selected items
     * - If any selected item's state becomes Disabled, it is automatically deselected
     * - Uses takeUntilDestroyed to properly clean up the subscription
     *
     * @private
     */
    private _setupRowStateChange(): void {
        this._stateService.rowStateChange$.pipe(
            // Complete the observable when disconnect emits
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {
            this.selectedData.selected.forEach((value) => {
                if (this._stateService.getRowState(value)?.type === RowStateType.Disabled) {
                    this.selectedData.deselect(value);
                }
            });
        });
    }

    /**
     * Sets up a subscription to the refresh observable.
     * When a refresh event occurs, it clears all row states in the state service.
     * The subscription is automatically cleaned up when the component is destroyed.
     * @private
     */
    private _setupRefresh(): void {

        this._reload$.pipe(
            // Complete the observable when disconnect emits
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {

            this._stateService.clearRowStates();
        });
    }

    /**
     * Sets up a subscription to the listAction$ push channel on the state service.
     *
     * The state service has already applied the cache invalidation matrix before emitting (so
     * actions recorded while this data source did not exist are also reflected in the cache).
     * This subscription only triggers a reload via `_reload$` (which calls `clearRowStates()` via
     * `_setupRefresh`) and re-applies the row states carried in the event.
     *
     * When the cache was mutated in place the reload finds a cache hit and returns without a
     * network call; when it was cleared the reload fetches fresh data.
     *
     * The subscription is automatically cleaned up when the component is destroyed.
     * @private
     */
    private _setupListAction(): void {
        this._stateService.listAction$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((event: IListActionEvent<M>) => {
            // Always trigger a reload. _setupRefresh handles clearRowStates().
            this._reload$.next(this._stateService.filterRequest);

            // Re-apply row states after clearRowStates() (called synchronously by _setupRefresh).
            event.rowStates.forEach((state, id) => {
                this._stateService.addRowState(id, state);
            });
        });
    }
}
